import React from 'react';
import { ContextUsage } from '../types';

/**
 * Inline context-window meter shown under the chat header.
 *
 * Layer-1 alignment (May 2026): the bar now shows the WORSE of
 * (post-compaction %, raw pre-compaction %). The raw % is what the
 * server's middleware would have to swallow on the next turn — so the
 * user sees the wall coming, not the squeezed projection from the
 * previous turn.
 *
 * Two stripes:
 *   - solid bar  = post-compaction % (the projection after the squeeze)
 *   - ghost bar  = raw pre-compaction % (what we just sent over the wire)
 *
 * The lower of the two is always ≤ the higher; the gap is exactly the
 * compaction savings, which gives the user a tangible signal that the
 * system is working in the background.
 *
 * Color thresholds (apply to the higher stripe):
 *   < 60%  green  — plenty of room
 *   60–80% yellow — start watching
 *   80–95% orange — recommend "Continue in fresh chat"
 *   ≥95%   red    — the next turn may bounce; recommend Continue now
 */
export function ContextMeter({
  usage,
  onContinueNow,
}: {
  usage: ContextUsage;
  /** When provided AND the meter is in the orange/red band, an inline
   *  "Continue now" button appears. Click it to swap the active
   *  conversation into a fresh chat with a checkpoint primer. */
  onContinueNow?: () => void;
}) {
  // The meter must report the WORSE of post-compact and raw. Raw is what
  // the next request will ship over the wire before the compactor sees it.
  const postPct = Math.max(0, Math.min(1.5, usage.pct));
  const rawPct = Math.max(0, Math.min(1.5, usage.rawPct ?? usage.pct));
  const headlinePct = Math.max(postPct, rawPct);
  const displayPct = Math.min(1, headlinePct);
  const pctText = `${(headlinePct * 100).toFixed(1)}%`;

  const { barColor, hint, hintColor } = (() => {
    if (headlinePct < 0.6) {
      return { barColor: '#16a34a', hint: null, hintColor: '#16a34a' };
    }
    if (headlinePct < 0.8) {
      return {
        barColor: '#eab308',
        hint: 'Conversation is getting long.',
        hintColor: '#a16207',
      };
    }
    if (headlinePct < 0.95) {
      return {
        barColor: '#f97316',
        hint: 'Close to the soft limit. Click ↪ on this chat in the side panel to continue in a fresh chat — your work carries forward.',
        hintColor: '#c2410c',
      };
    }
    return {
      barColor: '#dc2626',
      hint: 'Next turn may bounce. Use ↪ Continue in fresh chat to carry the checkpoint over without losing state.',
      hintColor: '#b91c1c',
    };
  })();

  const inputCharsText = usage.inputChars.toLocaleString();
  const rawCharsText = (usage.rawInputChars ?? usage.inputChars).toLocaleString();
  const maxCharsText = usage.maxInputChars.toLocaleString();
  const messageText =
    usage.messageCount > 0 ? `${usage.messageCount}/${usage.maxMessages} msgs` : '';
  const compactionNote =
    usage.compactedBlocks > 0
      ? ` · saved ${(usage.charsSaved / 1000).toFixed(0)}k via compaction`
      : '';

  // Ghost stripe — pre-compaction. Sits behind the solid bar so the user
  // sees the size BEFORE squeeze AND the size AFTER.
  const ghostPct = Math.min(1, rawPct);
  const solidPct = Math.min(1, postPct);

  return (
    <div
      style={{
        padding: '6px 12px 8px 12px',
        background: '#f7f8fa',
        borderBottom: '1px solid #e5e7eb',
        fontSize: 11,
        color: '#4b5563',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
      title={`Raw ${rawCharsText} chars → after compaction ${inputCharsText} chars (soft target ${maxCharsText}). The bar tracks the worse of the two so you see the wall, not the squeezed projection.`}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 4,
        }}
      >
        <span style={{ fontWeight: 500 }}>Context: {pctText}</span>
        <span style={{ color: '#9ca3af' }}>
          {messageText}
          {compactionNote}
        </span>
      </div>
      <div
        style={{
          width: '100%',
          height: 4,
          background: '#e5e7eb',
          borderRadius: 2,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {/* Ghost stripe — raw size (what we sent over the wire) */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: `${ghostPct * 100}%`,
            height: '100%',
            background: barColor,
            opacity: 0.25,
            transition: 'width 200ms ease-out, background 200ms ease-out',
          }}
        />
        {/* Solid stripe — post-compaction projection */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: `${solidPct * 100}%`,
            height: '100%',
            background: barColor,
            transition: 'width 200ms ease-out, background 200ms ease-out',
          }}
        />
        {/* Reference: the displayPct ensures the visible fill never exceeds the bar width */}
        <div style={{ width: `${displayPct * 100}%`, height: '100%', visibility: 'hidden' }} />
      </div>
      {hint && (
        <div
          style={{
            marginTop: 4,
            color: hintColor,
            fontSize: 11,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 6,
          }}
        >
          <span style={{ flex: 1 }}>{hint}</span>
          {onContinueNow && headlinePct >= 0.8 && (
            <button
              onClick={onContinueNow}
              style={{
                fontSize: 11,
                padding: '3px 8px',
                background: barColor,
                color: '#fff',
                border: 'none',
                borderRadius: 12,
                cursor: 'pointer',
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
              title="Save a checkpoint of this conversation and continue in a fresh chat. Your work, sheets touched, last error and original goal carry forward — no token re-payment."
            >
              ↪ Continue now
            </button>
          )}
        </div>
      )}
    </div>
  );
}
